import React, { useState, useRef, useEffect, forwardRef } from 'react';
import PropTypes from 'prop-types';
import ChipLabel from './ChipLabel/ChipLabel';
import { ChipRoot } from './ChipSelection.style';
import { chipSelectionClasses as classes } from './chipSelectionClasses';
import { useMultipleRefsSetter } from '../../customHooks/useMultipleRefsSetter';

const ChipSelection = forwardRef(
  (
    {
      label,
      startAdornment,
      endAdornment,
      chipDefaultValue,
      controlType,
      renderLabel,
      onDelete,
      deleteIcon,
      renderComponent,
      size = 'medium',
      value,
      onChange,
      onClick,
      disabled,
      maxSelectedItemsToDisplay,
      helpTooltipContent,
      HelpTooltipProps,
      LabelTooltipProps,
      CounterBadgeProps,
      openOnMount = false,
      ...rest
    },
    externalRef
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const anchorRef = useRef(null);
    const rootRefCallback = useMultipleRefsSetter(externalRef, anchorRef);

    useEffect(() => {
      if (openOnMount && anchorRef) {
        setIsOpen(true);
      }
    }, [openOnMount, anchorRef]);

    return (
      <>
        <ChipRoot
          size={size}
          {...{ isOpen, onDelete, deleteIcon }}
          type="button"
          component="button"
          tabIndex={disabled ? -1 : undefined}
          data-qa-id="chip-selection-button"
          label={
            <ChipLabel
              {...{
                label,
                startAdornment,
                endAdornment,
                chipDefaultValue,
                renderLabel,
                value,
                controlType,
                disabled,
                maxSelectedItemsToDisplay,
                helpTooltipContent,
                HelpTooltipProps,
                LabelTooltipProps,
                CounterBadgeProps
              }}
            />
          }
          ref={rootRefCallback}
          color="primary"
          variant="outlined"
          disabled={disabled}
          onClick={() => {
            setIsOpen(true);
            onClick?.();
          }}
          {...rest}
        />

        {renderComponent({
          value,
          onChange,
          isOpen,
          setIsOpen,
          anchorRef
        })}
      </>
    );
  }
);

ChipSelection.propTypes = {
  label: PropTypes.string,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  /**
   * Default display in the Chip, in case value is empty.
   */
  chipDefaultValue: PropTypes.string,
  controlType: PropTypes.oneOf([
    'multiSelect',
    'select',
    'dateRange',
    'datePicker',
    'appSelect',
    'multiAppSelect',
    'bundle'
  ]),
  /**
   * Custom value render function.
   *
   * Signature: `function({chipDefaultValue:string, value: any}) => jsx`
   */
  renderLabel: PropTypes.func,
  onDelete: PropTypes.func,
  deleteIcon: PropTypes.node,
  disabled: PropTypes.bool,
  /**
   * The number of selected labels that would be shown.
   * In case the number of selected labels would be bigger than `maxSelectedItemsToDisplay` then the counter badge will be shown.
   */
  maxSelectedItemsToDisplay: PropTypes.number,
  size: PropTypes.oneOf(['medium', 'small']),
  renderComponent: PropTypes.func,
  value: PropTypes.any,
  /**
   * Callback function for when the value is changed.
   *
   * Signature: `function({value}) => void`
   */
  onChange: PropTypes.func,
  /**
   * Callback function for when the Chip is being clicked.
   *
   * Signature: `function() => void`
   */
  onClick: PropTypes.func,
  helpTooltipContent: PropTypes.node,
  /**
   * props available in [Tooltip](./index.html?path=/docs/components-feedback-tooltip--docs) component
   **/
  HelpTooltipProps: PropTypes.object,
  LabelTooltipProps: PropTypes.object,
  /**
   * props available in [Badge](./index.html?path=/docs/components-data-display-badge--docs) component
   **/
  CounterBadgeProps: PropTypes.object,
  openOnMount: PropTypes.bool
};

ChipSelection.classes = classes;
ChipSelection.displayName = 'ChipSelection';

export default ChipSelection;
